// Field geometry utilities — pure functions, no DOM dependency.
// fieldMetrics() takes canvasHeight as a parameter instead of reading refs.canvas.height.

import { snapToPixel } from '../util/math.js';

export interface FieldMetrics {
  yardWidth: number;
  endZoneWidth: number;
  outerPadding: number;
  crowdTop: number;
  fieldTop: number;
  fieldBottom: number;
  fieldHeight: number;
  centerY: number;
  goalLeft: number;
  goalRight: number;
  endZoneLeft: number;
  endZoneRight: number;
  worldWidth: number;
}

export function fieldMetrics(canvasHeight: number = 544): FieldMetrics {
  const yardWidth = 22;
  const endZoneWidth = yardWidth * 10;
  const outerPadding = 128;
  const crowdTop = 78;
  const fieldTop = 94;
  const fieldBottom = canvasHeight - 28;
  const goalLeft = outerPadding + endZoneWidth;
  const goalRight = goalLeft + yardWidth * 100;
  const endZoneLeft = goalLeft - endZoneWidth;
  const endZoneRight = goalRight + endZoneWidth;

  return {
    yardWidth,
    endZoneWidth,
    outerPadding,
    crowdTop,
    fieldTop,
    fieldBottom,
    fieldHeight: fieldBottom - fieldTop,
    centerY: Math.round((fieldTop + fieldBottom) / 2),
    goalLeft,
    goalRight,
    endZoneLeft,
    endZoneRight,
    worldWidth: endZoneRight + outerPadding,
  };
}

export function yardToX(yardLine: number, canvasHeight?: number): number {
  const m = fieldMetrics(canvasHeight);
  return m.goalLeft + m.yardWidth * yardLine;
}

export function worldToScreenX(worldX: number, cameraX: number = 0): number {
  return snapToPixel(worldX - cameraX);
}

export function screenXForYard(yardLine: number, direction: number, canvasHeight?: number): number {
  return direction === 1 ? yardToX(yardLine, canvasHeight) : yardToX(100 - yardLine, canvasHeight);
}

export function goalLineXForDirection(direction: number, canvasHeight?: number): number {
  const m = fieldMetrics(canvasHeight);
  return direction === 1 ? m.goalRight : m.goalLeft;
}

export function orientX(anchorX: number, offset: number, direction: number): number {
  return snapToPixel(anchorX + offset * direction);
}

export function clampAdvanceX(losX: number, endX: number, minOffset: number, maxOffset: number, direction: number): number {
  const minX = orientX(losX, minOffset, direction);
  const maxX = orientX(losX, maxOffset, direction);
  const low = Math.min(minX, maxX);
  const high = Math.max(minX, maxX);
  return Math.max(low, Math.min(high, endX));
}

export function goalPostWorldX(direction: number, canvasHeight?: number): number {
  const m = fieldMetrics(canvasHeight);
  return direction === 1 ? m.endZoneRight - 20 : m.endZoneLeft + 20;
}

export function kickAttemptCameraTargetX(
  losX: number,
  direction: number,
  canvasWidth: number,
  canvasHeight?: number,
): number {
  const m = fieldMetrics(canvasHeight);
  const postX = goalPostWorldX(direction, canvasHeight);
  const focusX = snapToPixel(losX + (postX - losX) * 0.54);
  const maxCameraX = Math.max(0, m.worldWidth - canvasWidth);
  return snapToPixel(Math.max(0, Math.min(maxCameraX, focusX - canvasWidth / 2)));
}

export function kickAttemptZoomFocusX(
  losX: number,
  direction: number,
  cameraX: number,
  canvasWidth: number,
  canvasHeight?: number,
): number {
  const postScreenX = worldToScreenX(goalPostWorldX(direction, canvasHeight), cameraX);
  const losScreenX = worldToScreenX(losX, cameraX);
  const focusX = snapToPixel(postScreenX + (losScreenX - postScreenX) * 0.56);
  const minFocusX = canvasWidth * 0.3;
  const maxFocusX = canvasWidth * 0.7;
  return snapToPixel(Math.max(minFocusX, Math.min(maxFocusX, focusX)));
}

export function kickAttemptOperationWorldBounds(losX: number, direction: number): { leftX: number; rightX: number } {
  const kickerX = orientX(losX, -102, direction);
  const leaperX = orientX(losX, 88, direction);
  const leftX = Math.min(kickerX, leaperX) - 24;
  const rightX = Math.max(kickerX, leaperX) + 24;
  return { leftX, rightX };
}

export function kickAttemptZoomScale(
  distance: number | undefined,
  playId: string | undefined,
  losX: number,
  direction: number,
  cameraX: number,
  canvasWidth: number,
  canvasHeight?: number,
): number {
  const kickDistance = Number.isFinite(distance) ? Number(distance) : (playId === 'pat' ? 20 : 33);
  const normalized = Math.max(0, Math.min(1, (kickDistance - 20) / 35));
  const baseScale = playId === 'pat'
    ? 0.88
    : 0.9 - normalized * 0.16;
  const focusX = kickAttemptZoomFocusX(losX, direction, cameraX, canvasWidth, canvasHeight);
  const { leftX, rightX } = kickAttemptOperationWorldBounds(losX, direction);
  const leftScreenX = worldToScreenX(leftX, cameraX);
  const rightScreenX = worldToScreenX(rightX, cameraX);
  const margin = Math.max(36, canvasWidth * 0.04);
  let fitScale = 1;
  if (leftScreenX < focusX - 1) {
    fitScale = Math.min(fitScale, (focusX - margin) / Math.max(1, focusX - leftScreenX));
  }
  if (rightScreenX > focusX + 1) {
    fitScale = Math.min(fitScale, (canvasWidth - margin - focusX) / Math.max(1, rightScreenX - focusX));
  }
  return Math.max(0.5, Math.min(baseScale, fitScale, 1));
}

export function ballFrontX(ballX: number, direction: number): number {
  return snapToPixel(ballX + (direction > 0 ? 8 : 0));
}

export function ballBreaksPlane(ballX: number, goalLineX: number, direction: number, margin: number = 0): boolean {
  return direction > 0
    ? ballFrontX(ballX, direction) >= goalLineX - margin
    : ballFrontX(ballX, direction) <= goalLineX + margin;
}
